/* =============================================================================
   navModel.js
   Role-aware primary navigation for the consistent application shell (TB-075, TB-196).
   Each party (patient / doctor / establishment) gets the same shell; only the
   grouped item list, the quick action and the count keys differ.

   Item fields:
     label     visible text
     to        route path (every path below exists in router.js / doctorRouter.js)
     icon      key resolved to an inline SVG inside AppShell.vue
     exact     active only on an exact path match (dashboards)
     count     optional key into the nav counts store (useNavCounts)
     match     optional extra path prefixes that also mark the item active
     query     optional query the item links with; it is then active only when
               the current route carries the same query
     requiresRoute  optional route name; the item is hidden until it exists
     doctorOnly     optional; hidden when an establishment views a doctor
   Routes mirror existing pages; no business logic changes.
   ========================================================================== */
import { getEstProviderView } from '@/utils/estDoctorSelection'

function readUser () {
  try {
    return (
      JSON.parse(localStorage.getItem('user')) ||
      JSON.parse(localStorage.getItem('userInfo')) ||
      {}
    )
  } catch (e) {
    return {}
  }
}

// TB-085 (DEF-229): per-tab selection, not the shared localStorage key
function readEstProvider () {
  return getEstProviderView()
}

export function currentRole () {
  const user = readUser()
  const role = user.userRole || user.role
  const estProvider = readEstProvider()
  if (estProvider && estProvider.userRole === 'estProvider') return 'provider'
  if (role === 'establishment') return 'establishment'
  if (role === 'provider') return 'provider'
  return 'patient'
}

export const ROLE_LABELS = {
  patient: 'Patient',
  provider: 'Doctor',
  establishment: 'Establishment'
}

// TB-163/TB-169b: the patient side menu is navigation only. Settings, Help, theme
// and Log out live in the top right avatar menu (like the doctor); profile,
// reminders & notifications, payment cards and password are tabs of the Settings
// hub (HUBS below); the notification bell stays in the top bar.
const PATIENT_GROUPS = [
  {
    id: 'main',
    label: '',
    items: [
      { label: 'Home', to: '/dashboard', icon: 'home', exact: true },
      { label: 'Appointments', to: '/appointments', icon: 'calendar', count: 'upcomingAppointments', section: 'appointments', match: ['/appointment-details'] },
      { label: 'Prescriptions', to: '/prescriptions', icon: 'file', section: 'prescriptions' },
      // TB-106: invoices of the patient and their loved ones; /invoice/:ID belongs here.
      { label: 'Invoices', to: '/invoices', icon: 'wallet', section: 'invoices', match: ['/invoice'] },
      { label: 'Loved ones', to: '/loved-ones', icon: 'heart' },
      { label: 'Chat', to: '/chat', icon: 'chat', section: 'chat' }
    ]
  }
]

// TB-118: one flat doctor menu, no duplicates. Walk-in and Add hours live in the
// top bar '+ New' menu; Invoices and Payouts are tabs of Earnings; profile,
// consultation types, reminders & notifications (notification preferences are
// its third tab), subscription and invitations are tabs of Settings (HUBS
// below). Old routes stay reachable.
const EARNINGS_MATCH = ['/doctor/earnings', '/doctor/invoices', '/doctor/invoice', '/doctor/payouts']

const DOCTOR_GROUPS = [
  {
    id: 'main',
    label: '',
    // TB-196: daily work only. Settings is in the avatar menu for every role.
    items: [
      { label: 'Today', to: '/doctor', icon: 'home', exact: true },
      // TB-109 (A): one Schedule screen replaces Calendar (month) + Week view + Create slot.
      { label: 'Schedule', to: '/doctor/schedule', icon: 'calendar', section: 'schedule', match: ['/doctor/calendar', '/doctor/calendar-view', '/doctor/create-slot', '/doctor/book-appoinment', '/doctor/reschedule'] },
      { label: 'Requests', to: '/doctor/consultation', icon: 'inbox', count: 'pendingRequests', section: 'requests', match: ['/doctor/consultation-details'] },
      { label: 'Patients', to: '/doctor/patients', icon: 'users', match: ['/doctor/patient-profile', '/doctor/patients-previuos-details', '/doctor/patients-prescription'] },
      { label: 'Chat', to: '/doctor/chat', icon: 'chat', section: 'chat' },
      // TB-062: the badge counts invoices to review (invoice/pendings).
      { label: 'Earnings', to: '/doctor/my-earnings', icon: 'wallet', count: 'pendingInvoices', section: 'earnings', match: EARNINGS_MATCH },
      { label: 'Insights', to: '/doctor/insights', icon: 'chart', match: ['/doctor/stats'] }
    ]
  }
]

const ESTABLISHMENT_GROUPS = [
  {
    id: 'main',
    label: '',
    // TB-073: /doctor/establishment-doctor renders the establishment home;
    // ?view=doctors renders the full doctors table (Doctors item below).
    // TB-196: no Patients item, the clinic has no clinic-wide patients page.
    items: [
      { label: 'Today', to: '/doctor/establishment-doctor', icon: 'home', exact: true },
      { label: 'Doctors', to: '/doctor/establishment-doctor', query: { view: 'doctors' }, icon: 'users', count: 'pendingDoctors', section: 'doctors' },
      { label: 'Schedule', to: '/doctor/calendar-view', icon: 'calendar', section: 'schedule', match: ['/doctor/schedule', '/doctor/create-slot'] },
      { label: 'Bookings', to: '/doctor/consultation', icon: 'inbox', section: 'requests', match: ['/doctor/consultation-details'] },
      { label: 'Earnings', to: '/doctor/my-earnings', icon: 'wallet', section: 'earnings', match: EARNINGS_MATCH },
      { label: 'Insights', to: '/doctor/insights', icon: 'chart', match: ['/doctor/stats'] }
    ]
  }
]

// TB-118: pages grouped under one menu item get a tab strip in the shell.
// A tab is active on its own path, its `match` prefixes, or exact `to`.
// TB-196: Settings tabs, same order and names for every role, Security last.
// Locations is a section of the Profile page (hash), so it is its own tab.
const SETTINGS_HEAD = [
  { label: 'Profile', to: '/doctor/profile', match: ['/doctor/settings'], unlessHash: '#locations' },
  { label: 'Consultation types', to: '/doctor/poc' },
  { label: 'Locations', to: '/doctor/profile', hash: '#locations' }
]
const SECURITY_TAB = { label: 'Security', to: '/doctor/change-password' }
const EARNINGS_HUB_PROVIDER = {
  id: 'earnings',
  label: 'Earnings',
  tabs: [
    { label: 'Earnings', to: '/doctor/my-earnings', match: ['/doctor/earnings'] },
    { label: 'Invoices', to: '/doctor/invoices', count: 'pendingInvoices', match: ['/doctor/invoice'] },
    { label: 'Payouts', to: '/doctor/payouts' }
  ]
}
const EARNINGS_HUB_EST = {
  id: 'earnings',
  label: 'Earnings',
  tabs: [
    { label: 'Earnings', to: '/doctor/my-earnings', match: ['/doctor/earnings'] },
    { label: 'Invoices', to: '/doctor/invoices', match: ['/doctor/invoice'] },
    { label: 'Payouts', to: '/doctor/payouts' }
  ]
}

const HUBS = {
  patient: [
    {
      id: 'settings',
      label: 'Settings',
      tabs: [
        { label: 'Profile', to: '/profile', match: ['/edit-profile', '/settings'] },
        { label: 'Reminders & notifications', to: '/reminders' },
        { label: 'Payment cards', to: '/add-card' },
        { label: 'Security', to: '/change-password' }
      ]
    }
  ],
  provider: [
    EARNINGS_HUB_PROVIDER,
    {
      id: 'settings',
      label: 'Settings',
      tabs: [
        ...SETTINGS_HEAD,
        { label: 'Reminders & notifications', to: '/doctor/reminders', match: ['/doctor/notifications-setting', '/doctor/notification-preferences'], requiresRoute: 'doctor-reminders' },
        { label: 'Subscription', to: '/doctor/subscriptions', match: ['/doctor/add-payment-card'] },
        { label: 'Clinics & invitations', to: '/doctor/clinics', match: ['/doctor/invitations'], doctorOnly: true },
        SECURITY_TAB
      ]
    }
  ],
  establishment: [
    EARNINGS_HUB_EST,
    {
      id: 'settings',
      label: 'Settings',
      tabs: [
        ...SETTINGS_HEAD,
        { label: 'Reminders & notifications', to: '/doctor/reminders', match: ['/doctor/notifications-setting', '/doctor/notification-preferences'], requiresRoute: 'doctor-reminders' },
        { label: 'Subscription', to: '/doctor/subscriptions', match: ['/doctor/add-payment-card'] },
        // The Doctors page is also a left menu item: this tab only links to it.
        { label: 'Doctors & invitations', to: '/doctor/establishment-doctor', query: { view: 'doctors' }, linkOnly: true },
        SECURITY_TAB
      ]
    }
  ]
}

// TB-118: the '+ New' menu (top bar, and the Home header). `gated` items go to
// the subscription page while the account is not subscribed.
const NEW_ACTIONS = {
  patient: [
    { id: 'book-appointment', label: 'Book appointment', to: { name: 'search', params: { searchText: ' ' } }, icon: 'calendar-plus' }
  ],
  provider: [
    { id: 'walk-in', label: 'Book patient', hint: 'Book a patient at the desk', to: '/doctor/book-appoinment', icon: 'walkin', gated: true },
    { id: 'add-hours', label: 'Add hours', hint: 'Open bookable time', to: { path: '/doctor/schedule', query: { add: 'hours' } }, icon: 'clock', gated: true }
  ],
  establishment: [
    { id: 'walk-in', label: 'Book patient', hint: 'Pick a doctor, then book a patient', to: '/doctor/calendar-view', icon: 'walkin' },
    { id: 'add-hours', label: 'Add hours', hint: 'Pick a doctor, then open time', to: '/doctor/calendar-view', icon: 'clock' },
    { id: 'add-doctor', label: 'Add doctor', hint: 'Invite a doctor to your clinic', to: '/doctor/add-doctor', icon: 'userplus' }
  ]
}

const QUICK_ACTIONS = {
  // A single space returns every doctor from POST providers/search.
  patient: { label: 'Book appointment', to: { name: 'search', params: { searchText: ' ' } }, icon: 'plus' },
  provider: { label: 'Add hours', to: '/doctor/schedule', icon: 'plus' }, // TB-109 (A)
  establishment: { label: 'Add doctor', to: '/doctor/add-doctor', icon: 'userplus' }
}

export function newActionsForRole (role) {
  return NEW_ACTIONS[role] || []
}

function estViewStored () {
  const stored = readEstProvider()
  return !!(stored && stored.userRole === 'estProvider')
}

/** The hub (tab group) the route belongs to, with its visible tabs, or null. */
export function hubForRoute (role, route, hasRoute, estView) {
  const est = estView === undefined ? estViewStored() : !!estView
  const hubs = HUBS[role] || []
  for (const hub of hubs) {
    const tabs = hub.tabs.filter((t) => (!t.requiresRoute || (hasRoute && hasRoute(t.requiresRoute))) && !(t.doctorOnly && est))
    if (tabs.some((t) => !t.linkOnly && isItemActive(t, route))) return { ...hub, tabs }
  }
  return null
}

export function navGroupsForRole (role) {
  if (role === 'provider') return DOCTOR_GROUPS
  if (role === 'establishment') return ESTABLISHMENT_GROUPS
  return PATIENT_GROUPS
}

/**
 * Groups with items whose `requiresRoute` is not registered removed.
 * `hasRoute` is router.hasRoute (or any name -> boolean function).
 */
export function visibleGroups (role, hasRoute, estView) {
  // TB-081: `estView` defaults to the stored "establishment viewing a doctor" state.
  const est = estView === undefined ? estViewStored() : !!estView
  return navGroupsForRole(role)
    .map((g) => ({
      ...g,
      items: g.items.filter((i) => (!i.requiresRoute || (hasRoute && hasRoute(i.requiresRoute))) && !(i.doctorOnly && est))
    }))
    .filter((g) => g.items.length)
}

/** Flat list (kept for callers that used the old API). */
export function navForRole (role) {
  return navGroupsForRole(role).reduce((all, g) => all.concat(g.items), [])
}

export function quickActionForRole (role) {
  return QUICK_ACTIONS[role] || QUICK_ACTIONS.patient
}

/** Count keys a role needs, so the counts store only fetches what is shown. */
export function countKeysForRole (role) {
  // The top bar bell always shows the unread count.
  const keys = new Set(['unreadNotifications'])
  navForRole(role).forEach((i) => { if (i.count) keys.add(i.count) })
  return Array.from(keys)
}

/** Sections a role shows a NEW number for (keys of useNavCounts news). */
export function sectionsForRole (role) {
  const sections = new Set()
  navForRole(role).forEach((i) => { if (i.section) sections.add(i.section) })
  return Array.from(sections)
}

/** Router location for an item. */
export function itemLocation (item) {
  if (!item.query && !item.hash) return item.to
  const loc = { path: item.to }
  if (item.query) loc.query = item.query
  if (item.hash) loc.hash = item.hash
  return loc
}

/**
 * True when the route belongs to `item` (exact, prefix, or an extra match
 * prefix). `route` is { path, query } (or a bare path string).
 */
export function isItemActive (item, route) {
  const path = typeof route === 'string' ? route : (route && route.path) || ''
  const query = (route && typeof route === 'object' && route.query) || {}
  const hash = (route && typeof route === 'object' && route.hash) || ''
  if (item.hash) return path === item.to && hash === item.hash
  if (item.unlessHash && hash === item.unlessHash && path === item.to) return false
  if (item.query) {
    if (path !== item.to) return false
    return Object.keys(item.query).every((k) => String(query[k]) === String(item.query[k]))
  }
  if (item.exact) return (path === item.to || path === `${item.to  }/`) && !query.view
  const prefixes = [item.to].concat(item.match || [])
  return prefixes.some((p) => path === p || path.startsWith(`${p  }/`))
}

export default navGroupsForRole
